import React, { useState } from "react";
import { Button, Modal, Form, Image, Alert } from "react-bootstrap";
import { useMutation } from "react-query";
import { useAuth } from "../../context/auth";

interface ProfileImageUploadProps {
  show: boolean;
  handleClose: () => void;
}

const ProfileImageUpload: React.FC<ProfileImageUploadProps> = ({show, handleClose}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewURL, setPreviewURL] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = ["image/jpeg", "image/png", "image/webp"];
      if (!allowedTypes.includes(file.type)) {
        setError("Please upload a valid image (JPEG, PNG, or WebP).");
        return;
      }
      // Validate file size (5MB max)
      if (file.size > 2 * 1024 * 1024) {
        setError("File size should not exceed 2MB.");
        return;
      }
      setSelectedFile(file);
      setPreviewURL(URL.createObjectURL(file));
    }
  };
  
  const {updateUserPicture, user} = useAuth();
  const { mutateAsync: uploadFileFn, } = useMutation(updateUserPicture,
        {
                onSuccess: () => {
                        handleClose();
                },
                onError: (error) => {
                        setError("Unexpected error. Please try again or contact admin.");
                },
        }
);

  const handleSave = () => {
        if(!user) {
                setError("Unexpected error. Please reload page.");
                return;
        }
    if (selectedFile) {
                const formData = new FormData();
                formData.append("userId", user.id);
                formData.append("file", selectedFile);
                uploadFileFn(formData)
                handleClose();
                setSelectedFile(null);
                setPreviewURL(null);
    }
  };

  const handleCancel = () => {
    handleClose();
    setSelectedFile(null);
    setPreviewURL(null);
    setError(null);
  };

  return (
    <Modal show={show} onHide={handleCancel} centered>
      <Modal.Header closeButton className="bg-dark border-secondary">
        <Modal.Title className="text-white">Upload Profile Image</Modal.Title>
      </Modal.Header>
      <Modal.Body className="bg-dark text-light">
        <Form>
          <Form.Group controlId="formFile" className="mb-3">
            <Form.Label className="text-white">Choose a profile image:</Form.Label>
            <Form.Control 
              type="file" 
              accept="image/*" 
              onChange={handleFileChange}
              className="bg-dark text-white border-secondary"
              style={{
                backgroundColor: '#212529',
                borderColor: '#6c757d',
                color: '#ffffff'
              }} />
          </Form.Group>
          {error && (
            <Alert variant="danger" className="bg-danger bg-opacity-25 text-light border-danger">
              {error}
            </Alert>
          )}
          {previewURL && (
            <div className="text-center">
              <Image
                src={previewURL}
                roundedCircle
                style={{ width: "150px", height: "150px", objectFit: "cover" }}
                alt="Profile Preview"
                className="border border-secondary"
              />
            </div>
          )}
        </Form>
      </Modal.Body>
      <Modal.Footer className="bg-dark border-secondary">
        <Button variant="outline-secondary" onClick={handleCancel}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={!selectedFile || !!error}
        >
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ProfileImageUpload;